import SearchInput from "@/components/common/search-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetFCTableData,
  useGetFCTableDetail,
} from "@/queries/data-center/functional-core.api";
import { Heading } from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  ReadOnlyTable,
  type ReadOnlyTableSortState,
} from "./components/ReadOnlyTable";

export const CFDetailTable = () => {
  const { id: tableName } = useParams();
  const { data: tableCols, isLoading } = useGetFCTableDetail(tableName);
  const { data: tableData, isLoading: isLoadingData } = useGetFCTableData(
    tableName || "",
  );
  const [searchInput, setSearchInput] = useState("");
  const [sortState, setSortState] = useState<ReadOnlyTableSortState>({
    columnId: "",
    direction: "off",
  });

  const filteredRows = useMemo(() => {
    if (!tableData) return [];
    return tableData
      .map((row, originalIndex) => ({ ...row, originalIndex }))
      .sort(
        (
          a: { [key: string]: string | number },
          b: { [key: string]: string | number },
        ) => {
          if (!sortState) return 0;
          const { columnId, direction } = sortState;
          if (direction === "off") return 0;

          const aValue = a[columnId];
          const bValue = b[columnId];

          if (aValue < bValue) return direction === "asc" ? -1 : 1;
          if (aValue > bValue) return direction === "asc" ? 1 : -1;
          return 0;
        },
      )
      .filter((row) => {
        return Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchInput.toLowerCase()),
        );
      });
  }, [searchInput, tableData, sortState]);

  return (
    <div className="w-full rounded-xl border border-neutral bg-neutral-object overflow-hidden shadow-neutral-md">
      {isLoading ? (
        <div className="flex justify-between items-start gap-3 border-b border-neutral w-full p-3">
          <div className="flex flex-col flex-auto">
            <Skeleton className="h-4 w-48 mb-2" />
            <Skeleton className="h-3 w-64" />
          </div>
          <Skeleton className="h-9 w-60" />
        </div>
      ) : (
        <div className="flex justify-between items-start gap-3 border-b border-neutral w-full p-3">
          <div className="flex flex-col justify-center max-h-10 truncate w-full">
            <Heading
              variant="h6"
              className="text-body-loud w-full truncate"
              title={tableName || "Nombre de la tabla"}
            >
              {tableName || "Nombre de la tabla"}
            </Heading>
          </div>
          <SearchInput
            data-testid="search-input-dc-core"
            variant="xs"
            placeholder="Buscar..."
            className="h-9 w-60 text-sm"
            value={searchInput || ""}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      )}
      <div className="bg-neutral-object-accent-silent">
        {isLoading ? (
          <div className="h-[calc(100vh-225px)]">{/* Loading... */}</div>
        ) : (
          <>
            <div className="w-full h-[calc(100vh-273px)] overflow-auto flex relative">
              <div className="w-full">
                {tableCols && tableCols.length > 0 && !isLoadingData && (
                  <ReadOnlyTable
                    columns={tableCols}
                    rows={filteredRows ?? []}
                    sort={sortState}
                    onToggleSortChange={setSortState}
                    hasEmptySearch={
                      filteredRows.length === 0 && searchInput.length > 0
                    }
                  />
                )}
              </div>
            </div>
            {isLoadingData ? (
              <div className="h-11">{/* Loading data */}</div>
            ) : (
              <div className="p-3 bg-neutral-object text-body text-sm">
                Mostrando{" "}
                <span className="text-body-loud font-semibold">
                  {tableData?.length || 0}
                </span>{" "}
                filas
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
